import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      background: transparent;
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const Services = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const developmentSkills = [
    'Tailored Software Engineering',
    'Web Development',
    'API Development',
    'CMS Development',
  ];
  const managementSkills = [
    'Consulting',
    'Project Management',
    'Optimize business processes',
    'Helping you to crystallize your ideas on your digital transformation journey',
  ];
  const frontendSkills = [
    'JavaScript (ES6+)',
    'TypeScript',
    'React',
    'TailwindCSS',
    'Gatsby',
    'GraphQL',
    'Next.js',
    'Angular',
    'Vuejs',
  ];
  const backendSkills = ['Java', 'PHP', 'C#', 'Python', 'Node.js'];
  const backendFrameworks = ['Spring Boot', '.NET', 'Laravel', 'Express.js'];
  const cms = ['WordPress', 'Strapi', 'Directus', 'Storyblok'];
  const devOps = ['GitHub', 'Jenkins', 'AWS', 'Azure'];
  const industries = ['Finance', 'E-Commerce', 'SaaS', 'AI (learning)'];
  return (
    <StyledAboutSection id="services" ref={revealContainer}>
      <h2 className="numbered-heading">Services</h2>
      <h3 className="small-heading">Things i can do for your project (big or small)</h3>
      <div className="inner">
        <StyledText>
          <h5>Development</h5>
          <ul className="skills-list">
            {developmentSkills && developmentSkills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <hr />
          <h5>Frontend</h5>
          <ul className="skills-list">
            {frontendSkills && frontendSkills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <hr />
          <h5>Backend</h5>
          <ul className="skills-list">
            {backendSkills && backendSkills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <hr />
          <h5>Frameworks</h5>
          <ul className="skills-list">
            {backendFrameworks && backendFrameworks.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <hr />
          <h5>CMS</h5>
          <ul className="skills-list">{cms && cms.map((skill, i) => <li key={i}>{skill}</li>)}</ul>
          <hr />
          <h5>DevOps</h5>
          <ul className="skills-list">
            {devOps && devOps.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <hr />
          <h5>Business operations</h5>
          <ul className="skills-list">
            {managementSkills && managementSkills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
          <hr />
          <h5>Industries</h5>
          <ul className="skills-list">
            {industries && industries.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/dejan.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default Services;
